// 7HAI-968: ordering for legal-document version strings ("2026-07").
//
// Mirror of the backend helper (pipe_time_factor backend/legal.py, 7HAI-964).
// The two implementations MUST agree: the backend decides whether a session is
// refused, the frontend decides whether the acceptance page is offered. If they
// disagree the user is refused everywhere with no screen that can fix it — the
// exact lockout this ticket exists to prevent. Any change here is a change
// there, in the same release.
//
// THE RULES, stated so both sides can be checked against them:
//
//  1. A well-formed version is EXACTLY `YYYY-MM` — four digits, a hyphen, a
//     TWO-digit month (`^\d{4}-\d{2}$`). Because the month is zero-padded and
//     fixed-width, well-formed versions order correctly as plain strings, so
//     comparison is lexicographic over the two.
//
//  2. Anything else — null, undefined, "", whitespace, "2026-9", "2026-07-01",
//     "garbage" — is UNKNOWN, and unknown sorts OLDER THAN EVERY well-formed
//     version. Two unknowns compare equal.
//
// Why unknown is oldest, in both directions:
//
//  - A STORED version that is missing predates versioning (acceptances written
//    before the version was ever stamped). Those records must re-prompt, not
//    coast as current. Trace's bug was the mirror image — `undefined` counted
//    as accepted — and it is why this file spells the rule out.
//
//  - A MALFORMED stored version fails CLOSED: it re-prompts rather than
//    silently comparing wrong. `"2026-9"` is the case that matters — naive
//    lexicographic ordering ranks it ABOVE `"2026-10"`, which would wave
//    through a user who is a release behind. Re-prompting a user whose stored
//    string is corrupt costs one screen, and accepting rewrites the field with
//    a well-formed CURRENT_* value, so the state is self-healing.
//
//  - A malformed REQUIRED_* constant would be our own typo, and there the same
//    rule fails OPEN: every stored version outranks unknown, so the gate goes
//    inert rather than locking out every customer at once. A gate that stops
//    gating is visible in testing; a gate that refuses everyone is an outage.
//
// Deliberately NOT done: no leniency, no normalising `"2026-9"` to `"2026-09"`.
// A tolerant parser here that the backend does not share is itself a lockout.

const VERSION_PATTERN = /^\d{4}-\d{2}$/;

/**
 * True when `value` is a well-formed `YYYY-MM` legal version string.
 * Everything else — null, undefined, empty, padded/extended/garbage forms —
 * is unknown, and unknown is older than every well-formed version.
 */
export const isWellFormedLegalVersion = (value: string | null | undefined): value is string =>
  typeof value === "string" && VERSION_PATTERN.test(value);

/**
 * Compare two legal-document versions.
 *
 * Returns a negative number when `a` is older than `b`, zero when they are the
 * same, and a positive number when `a` is newer. Unknown (missing or malformed)
 * versions are older than every well-formed version and equal to each other.
 */
export const compareLegalVersions = (
  a: string | null | undefined,
  b: string | null | undefined,
): number => {
  const aOk = isWellFormedLegalVersion(a);
  const bOk = isWellFormedLegalVersion(b);
  if (!aOk && !bOk) return 0;
  if (!aOk) return -1;
  if (!bOk) return 1;
  if (a === b) return 0;
  return a < b ? -1 : 1;
};

/**
 * True when the `stored` version satisfies `required` — i.e. it is well-formed
 * and at or above the required version. A missing or malformed `stored` value
 * never satisfies a well-formed requirement.
 */
export const isLegalVersionSatisfied = (
  stored: string | null | undefined,
  required: string | null | undefined,
): boolean => compareLegalVersions(stored, required) >= 0;
